import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  HStack,
  MenuItem,
  Portal,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { memo, useMemo, useState } from "react"
import { useForm, useWatch } from "react-hook-form"
import { FaCheckCircle, FaRocket } from "react-icons/fa"
import { z } from "zod"

import { Field } from "@/components/atoms/Field"
import { useCustomToast } from "@/hooks/useCustomToast"
import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import type { ClassSessionListResponse } from "@/types/class_session"
import type { SessionListResponse } from "@/types/session"
import { unwrapStandardResponse } from "@/utils/apiResponse"

// Form validation schema - only session_id since class_template is pre-selected
const generateSchema = z.object({
  session_id: z.string().min(1, "Please select a session"),
})

type GenerateFormData = z.infer<typeof generateSchema>

interface GenerateSessionsMenuItemProps {
  classTemplateId: string
  classTemplateName: string
}

const GenerateSessionsMenuItem = memo(
  ({ classTemplateId, classTemplateName }: GenerateSessionsMenuItemProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const { hasPermission } = usePermissions()
    const queryClient = useQueryClient()
    const { showSuccessToast, showErrorToast } = useCustomToast()

    // Check permission
    if (!hasPermission("class_sessions:generate")) {
      return null
    }

    // Fetch sessions for dropdown
    const { data: sessionsData, isLoading: isLoadingSessions } = useQuery({
      queryKey: ["sessions", { limit: 100 }],
      queryFn: async () => {
        const response = await api.sessions.readSessionsApiV1SessionsGet({
          skip: 0,
          limit: 100,
          sortBy: "created_at",
          sortOrder: "desc",
        })
        const payload = unwrapStandardResponse<SessionListResponse>(response)
        return payload?.items ?? []
      },
      enabled: isOpen,
    })

    // Fetch existing class sessions for this template to check for duplicates
    const { data: existingSessionsData, isLoading: isLoadingExisting } =
      useQuery({
        queryKey: ["classSessions", { classTemplateId, limit: 500 }],
        queryFn: async () => {
          const response =
            await api.classSessions.readClassSessionsApiV1ClassSessionsGet({
              skip: 0,
              limit: 500,
              classTemplateId: classTemplateId,
            })
          const payload =
            unwrapStandardResponse<ClassSessionListResponse>(response)
          return payload?.items ?? []
        },
        enabled: isOpen,
      })

    // Group existing class sessions by session_id to get counts
    const existingCountsBySession = useMemo(() => {
      const counts: Record<string, number> = {}
      // The queryFn above already extracts .items, so data is always an array
      const sessions = existingSessionsData ?? []
      for (const cs of sessions) {
        const sessionId = cs.session_id
        if (sessionId) {
          counts[sessionId] = (counts[sessionId] || 0) + 1
        }
      }
      return counts
    }, [existingSessionsData])

    // Form setup
    const {
      register,
      handleSubmit,
      formState: { errors },
      reset,
      control,
    } = useForm<GenerateFormData>({
      resolver: zodResolver(generateSchema),
      defaultValues: {
        session_id: "",
      },
    })

    // Watch selected session to show warning if it already has class sessions
    const selectedSessionId = useWatch({ control, name: "session_id" })
    const existingCount = selectedSessionId
      ? existingCountsBySession[selectedSessionId] || 0
      : 0

    // Generate mutation
    const generateMutation = useMutation({
      mutationFn: async (data: GenerateFormData) => {
        const response =
          await api.classTemplates.generateClassTemplateMeetingsApiV1ClassTemplatesClassTemplateIdGenerateMeetingsPost(
            {
              classTemplateId: classTemplateId,
              sessionId: data.session_id,
            },
          )
        return unwrapStandardResponse<Record<string, unknown>>(response)
      },
      onSuccess: (data) => {
        const meetingsCreated =
          (data?.meetings_created as number | undefined) ?? 0
        const totalOccurrences =
          (data?.session_occurrences as number | undefined) ?? 0

        showSuccessToast(
          `Created ${meetingsCreated} new class sessions for "${classTemplateName}". Session now has ${totalOccurrences} total occurrences.`,
        )

        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ["classSessions"] })

        handleClose()
      },
      onError: (error: Error) => {
        showErrorToast(error.message || "Failed to generate class sessions")
      },
    })

    const handleClose = () => {
      setIsOpen(false)
      reset()
    }

    const onSubmit = async (data: GenerateFormData) => {
      await generateMutation.mutateAsync(data)
    }

    // The queryFn above already extracts .items, so data is always an array
    const sessions = sessionsData ?? []

    const isLoading = isLoadingSessions || isLoadingExisting

    return (
      <>
        <MenuItem value="generate" onClick={() => setIsOpen(true)}>
          <FaRocket fontSize="14px" aria-hidden="true" />
          Generate Sessions
        </MenuItem>

        <DialogRoot
          open={isOpen}
          onOpenChange={(e) =>
            !generateMutation.isPending &&
            (e.open ? setIsOpen(true) : handleClose())
          }
          size="md"
        >
          <Portal>
            <DialogBackdrop />
            <DialogPositioner>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    Generate Sessions for "{classTemplateName}"
                  </DialogTitle>
                </DialogHeader>
                <DialogCloseTrigger disabled={generateMutation.isPending} />

                <DialogBody>
                  <form
                    id="generate-menu-form"
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    <VStack gap={4} alignItems="stretch">
                      <Box
                        p={3}
                        bg="blue.50"
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor="blue.200"
                      >
                        <Text fontSize="sm" color="blue.800">
                          Generate class sessions for{" "}
                          <strong>"{classTemplateName}"</strong> based on its
                          schedule (day, time) and the selected session's date
                          range.
                        </Text>
                      </Box>

                      {isLoading ? (
                        <HStack justifyContent="center" py={4}>
                          <Spinner size="sm" />
                          <Text fontSize="sm" color="gray.600">
                            Loading sessions...
                          </Text>
                        </HStack>
                      ) : (
                        <>
                          <Field
                            label="Session"
                            invalid={!!errors.session_id}
                            errorText={errors.session_id?.message}
                            required
                          >
                            <Box
                              as="select"
                              {...register("session_id")}
                              width="100%"
                              px={3}
                              py={2}
                              borderRadius="md"
                              borderWidth="1px"
                              borderColor={
                                errors.session_id ? "red.500" : "gray.200"
                              }
                              fontSize="sm"
                              _focus={{
                                borderColor: "blue.500",
                                outline: "none",
                              }}
                            >
                              <option value="">Select a session...</option>
                              {sessions.map((session) => {
                                const count =
                                  existingCountsBySession[session.id] || 0
                                return (
                                  <option key={session.id} value={session.id}>
                                    {session.name} ({session.start_week} -{" "}
                                    {session.end_week})
                                    {count > 0
                                      ? ` - ${count} sessions exist`
                                      : ""}
                                  </option>
                                )
                              })}
                            </Box>
                          </Field>

                          {existingCount > 0 && (
                            <Box
                              p={3}
                              bg="green.50"
                              borderRadius="md"
                              borderWidth="1px"
                              borderColor="green.200"
                            >
                              <HStack gap={2}>
                                <FaCheckCircle color="green" />
                                <Text fontSize="sm" color="green.800">
                                  <strong>
                                    {existingCount} class sessions
                                  </strong>{" "}
                                  already exist for this template and session.
                                  Generating again is safe - no duplicates will
                                  be created.
                                </Text>
                              </HStack>
                            </Box>
                          )}
                        </>
                      )}
                    </VStack>
                  </form>
                </DialogBody>

                <DialogFooter>
                  <HStack gap={2} justifyContent="flex-end">
                    <Button
                      variant="outline"
                      onClick={handleClose}
                      disabled={generateMutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      form="generate-menu-form"
                      colorPalette="green"
                      loading={generateMutation.isPending}
                      loadingText="Generating..."
                      disabled={isLoading}
                    >
                      Generate Sessions
                    </Button>
                  </HStack>
                </DialogFooter>
              </DialogContent>
            </DialogPositioner>
          </Portal>
        </DialogRoot>
      </>
    )
  },
)

GenerateSessionsMenuItem.displayName = "GenerateSessionsMenuItem"

export default GenerateSessionsMenuItem
export type { GenerateSessionsMenuItemProps }
